import { StaticInterface } from '../../../helpers/static-interface';
import EmblaCarousel from 'embla-carousel';
import Autoheight from 'embla-carousel-auto-height'
import Autoplay from 'embla-carousel-autoplay'
import ClassNames from "embla-carousel-class-names";

export interface ITpxKnowledgeHeroUtils {
  loadEmbla(parentEl: HTMLElement): void;
}

export interface IStaticKnowledgeHeroUtils {
  new(...args: any[]): ITpxKnowledgeHeroUtils;
  init(): void;
}

/**
 * Knowledge Hero utilities
 * @class
 */
export default class TpxKnowledgeHeroUtils
  implements StaticInterface<IStaticKnowledgeHeroUtils, typeof TpxKnowledgeHeroUtils> {
  constructor(element: HTMLElement) {
    this.loadEmbla(element);
  }

  /**
   * Load Embla
   */
  loadEmbla(parentEl: HTMLElement): void {

    if (!parentEl) return;

    const carousel = {
      options: {
        loop: false,
        slidesToScroll: 1,
        inViewThreshold: 1,
        draggable: false,
      },
      components: {
        componentEl: parentEl,
        containerEl: <HTMLElement | null>parentEl.querySelector('[data-component="Carousel"]'),
        buttonEls: <NodeList>parentEl.querySelectorAll('[data-component="Button"]'),
        activeButtonEl: <HTMLElement>parentEl.querySelectorAll('[data-component="Button"]')[0]
      }
    }

    // console.log("Component: Carousel", carousel)

    if (!carousel.components.containerEl) return;

    const embla = EmblaCarousel(carousel.components.containerEl, carousel.options, [ClassNames(), Autoheight(), Autoplay({ delay: 5000, stopOnMouseEnter: true, stopOnInteraction: false })]);

    // Set first active button
    carousel.components.activeButtonEl.classList.add('is-selected');

    if (carousel.components.buttonEls) {
      carousel.components.buttonEls.forEach((node, i) => {
        node.addEventListener('click', (e) => {

          // Remove active stae from last button
          carousel.components.activeButtonEl.classList.remove('is-selected');

          const target = e.target as HTMLElement;
          const el = target.closest('[data-component="Button"]') as HTMLElement;
          el?.classList.add('is-selected');
          carousel.components.activeButtonEl = el

          embla.scrollTo(i)
        });
      });
    }

    /* this is to stop the autoplay when any link in a slide receives focus and restart it again after focus is removed */
    const links = carousel.components.containerEl.querySelectorAll("a");
    links.forEach((link) => {
      link.addEventListener("focus", () => {
        embla?.plugins()?.autoplay?.stop();
      })

      link.addEventListener("blur", () => {
        embla?.plugins()?.autoplay?.play();
      })
    })


    const makeSlidesNotInViewUnfocusable = (indexOfCurrentSlide: number) => {
      const slides = embla.slideNodes();

      slides.forEach((slide, index) => {
        const linksInThisSlide = slide.querySelectorAll("a");

        if (index === indexOfCurrentSlide) {
          linksInThisSlide.forEach((link) => { link.tabIndex = 0 });
        } else {
          linksInThisSlide.forEach((link) => { link.tabIndex = -1 });
        }

      });
    }

    makeSlidesNotInViewUnfocusable(0); //to handle the case when the carousel first loads. when the slide changes, the embla.on bit below kicks in

    embla.on("select", () => {
      const currentIndex = embla.selectedScrollSnap();

      /* this is to keep the service buttons styling in sync with the slide currently in view */
      carousel.components.buttonEls.forEach((button, index) => {
        const theBtn = button as HTMLElement;
        const isSelected = index === currentIndex;
        if (isSelected) {
          theBtn.classList.add('is-selected')
        }
        else {
          theBtn.classList.remove('is-selected')
        }
      })


      /* this is to prevent all slides not currently in view from being focusable */
      makeSlidesNotInViewUnfocusable(currentIndex);
    })

  }

  /**
   * Create new instances of TpxKnowledgeHero for each element with class o-further-reading"
   */
  static init(scope?: HTMLElement) {
    [
      ...(scope || document).querySelectorAll('[data-component="KnowledgeHero"]'),
    ].forEach((elem) => {
      new TpxKnowledgeHeroUtils(elem as HTMLElement);
    });
  }
}
